import { ROOT_NODE, useNode, type Node, type NodeHelpersType } from '@craftjs/core';
import { getVisibilityClasses, DEFAULT_VISIBILITY } from '@builder4/shared/utils/styleHelpers';
import NodeVideoPlaceholder from '@/components/userComponents/Video/NodeVideoPlaceholder';
import { Resizer } from '../Resizer';
import { useRenderContext } from '@builder4/shared/context/RenderContext';
import clsx from 'clsx';
import SharedVideo, {
  defaultProps,
  type VideoProps,
  videoNodeStyle,
  getVideoNodeStylesObject,
} from '@builder4/shared/components/Video';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { VIDEO_NODE_NAME } from '@/utils/constants';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useAppSelector } from '@/store/hooks';
import { selectBreakpoint } from '@/store/system/selectors';
import { removeUndefined } from '@/utils/helperFunc';
import { parseVideoUrl, extractIframeSrc } from '@builder4/shared/utils/videoHelpers';
import { InspectorVideo } from '@/components/inspector/InspectorVideo/InspectorVideo';

// Wrapper component that provides the Resizer and Craft.js integration to the shared Video
export const Video = (props: Partial<VideoProps>) => {
  const { environment } = useRenderContext();
  const previewMode = environment === 'preview';

  props = {
    ...defaultProps,
    ...props,
  };

  const breakpoint = useAppSelector(selectBreakpoint);
  const { getBPProp } = useBreakpointAwareProp(VIDEO_NODE_NAME);
  const visibility = getBPProp(props, 'visibility') ?? DEFAULT_VISIBILITY;
  const fillSpace = getBPProp(props, 'fillSpace');
  const maxWidth = getBPProp(props, 'maxWidth');
  const maxWidthUnit = getBPProp(props, 'maxWidthUnit') || 'px';
  const maxWidthWithUnit = maxWidth != null ? `${maxWidth}${maxWidthUnit}` : undefined;

  const {
    connectors: { connect, drag },
    isActive,
  } = useNode((node) => ({
    isActive: node.events.selected,
  }));

  // Determine if video has a valid source
  const hasVideoSource =
    props.sourceType === 'url'
      ? Boolean(props.url && parseVideoUrl(props.url).isValid)
      : Boolean(props.embedCode && extractIframeSrc(props.embedCode));

  // Use the shared videoNodeStyles function for consistent styling
  const videoNodeStyleClass = videoNodeStyle(props, false);
  const visibilityClasses = getVisibilityClasses(visibility);

  const { mobile, tablet, ...computedStyles } = getVideoNodeStylesObject(props);
  let currentBpFlexStyle: object = computedStyles;
  if (breakpoint === 'mobile' && mobile) {
    currentBpFlexStyle = { ...computedStyles, ...removeUndefined(mobile) };
  } else if (breakpoint === 'tablet' && tablet) {
    currentBpFlexStyle = { ...computedStyles, ...removeUndefined(tablet) };
  }

  if (previewMode && !hasVideoSource) return null;

  return (
    <Resizer
      className={clsx('video-node', visibilityClasses, videoNodeStyleClass, {
        'flex-shrink-1-imp': fillSpace === 'yes',
      })}
      propKey={{ width: 'width', height: 'height' }}
      onRef={(ref) => {
        if (ref) {
          connect(drag(ref));
        }
      }}
      maxWidth={maxWidthWithUnit}
      style={currentBpFlexStyle}
    >
      <SharedVideo {...props}>
        {!hasVideoSource && <NodeVideoPlaceholder isActive={isActive} />}
      </SharedVideo>
    </Resizer>
  );
};

Video.craft = {
  ...SharedVideo.craft,
  related: {
    toolbar: InspectorVideo,
  },
  rules: {
    canDrag: (node: Node, helpers: NodeHelpersType) => {
      if (node.data.custom.isLocked) return false;
      const breakpoint = helpers(ROOT_NODE).get().data.props.breakpoint || 'desktop';
      const position = getBreakpointProp(
        node.data.props as Partial<ContainerProps>,
        'position',
        breakpoint,
      );

      return position === 'relative' || position === 'sticky';
    },
  },
};

export default Video;
